import React, { useState } from 'react';

// helpers
import styled from 'styled-components';
import useFetch from 'hooks/useFetch';
import useTranslation from 'hooks/useTranslation';
import ErrorHandlerService from 'services/error-handler/service';
import { transfersAPI } from 'api/accounting/transfersAPI';
import { FormValuesModel } from '..';
import { IApprovalMatrixRecord } from 'typings/accounting/transaction';
import { FormikHelpers, useFormikContext } from 'formik';
import { AccountManagementApprovalStatuses } from 'enums/approvalManagement/approvalManagement';

// components
import LoadingWrapper from 'components/WrapperComponents/LoadingWrapper';
import ApprovalReviewResults from 'components/Additional/ApprovalReviewResults';
import { ModalDialog, RequiredPropsForModalDialogModel, Text } from '@ui';

interface IProps extends RequiredPropsForModalDialogModel {
  emptyTransactionFormValues: FormValuesModel;
  onSubmit: (
    values: FormValuesModel,
    formikHelpers: FormikHelpers<FormValuesModel>,
  ) => void | Promise<unknown>;
}

const PreviewApprovalWorkflowModalDialog = ({
  isVisible,
  closeCallback,
  onSubmit,
  emptyTransactionFormValues,
}: IProps) => {
  const { t } = useTranslation(['transfers', 'common', 'custom_errors']);
  const form = useFormikContext<FormValuesModel>();
  const [btnLoading, setLoading] = useState(false);

  const { response, loading } = useFetch(
    () =>
      isVisible
        ? transfersAPI.fetchPreviewApprovalWorkflow({
            fromAccountNumber: form.values.sender.accountNumber,
            transactionAmount: form.values.amount || 0,
          })
        : null,
    [form.values, isVisible],
  );

  const formattedApprovalState = response?.data.approvalProgressStateSets.map(
    (e) => e.progressStateItems,
  );

  const handleSubmit = async (
    values: FormValuesModel,
    helpers: FormikHelpers<FormValuesModel>,
  ) => {
    try {
      setLoading(true);
      await onSubmit(values, helpers);
      if (emptyTransactionFormValues) {
        helpers.resetForm({
          values: emptyTransactionFormValues as FormValuesModel,
          errors: {},
        });
      }
      closeCallback(true);
    } catch (error: any) {
      const errorMessageCode = ErrorHandlerService.getErrorCodeFromError(error);
      if (errorMessageCode == '1301301') {
        ErrorHandlerService.displayErrorMessage(
          t('transfers.transaction_insufficient_balance', {
            ns: 'custom_errors',
          }),
        );
      } else {
        ErrorHandlerService.handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalDialog
      title={t('international_transfer.preview_approval_workflow_modal.title')}
      isVisible={isVisible}
      shouldResetFormAfterSubmit={false}
      closeCallback={closeCallback}
      submitButtonProps={{
        text: t('submit', { ns: 'common' }),
        onClick: () => handleSubmit(form.values, form),
        loading: btnLoading,
        disabled: btnLoading,
      }}
    >
      <LoadingWrapper loading={loading}>
        {response?.data.status ===
          AccountManagementApprovalStatuses.Approved && (
          <StyledText gutterBottom>
            {t(
              'international_transfer.preview_approval_workflow_modal.one_approver_info',
            )}
          </StyledText>
        )}
        <Text gutterBottom>
          {t(
            'international_transfer.preview_approval_workflow_modal.description',
          )}
        </Text>
        <ApprovalReviewResults
          shouldShowDate={false}
          transactionApprovals={
            formattedApprovalState as IApprovalMatrixRecord[][]
          }
        />
      </LoadingWrapper>
    </ModalDialog>
  );
};

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colorWarning};
`;

export default PreviewApprovalWorkflowModalDialog;
